import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { tokenStore, SESSION_WARNING_MS } from "../services/authClient";

/**
 * Fixed-cap session countdown.
 *
 * A signed-in session ends a fixed time after login (SESSION_CAP_MS, = the verified
 * 24h Cognito refresh-token validity). This banner watches the absolute
 * `session_expires_at` deadline in localStorage and, once the remaining time drops
 * inside SESSION_WARNING_MS, shows a live M:SS countdown on every screen EXCEPT the
 * public landing page. When the deadline passes it signs the user out.
 *
 * Mounted once, unconditionally, in App.js. The "hide on /" rule lives here rather
 * than at the mount site so the single timer keeps enforcing sign-out on every route
 * — including the landing page — instead of being unmounted (and reset) each time the
 * user crosses "/".
 *
 * It re-reads the deadline every tick rather than caching it, so a background token
 * refresh (which never writes this key) can't disturb it and a change in another tab
 * is picked up within a second.
 */

// Whole seconds, rounded up: the last visible value is "0:01", never a full second
// of "0:00" before sign-out.
const formatMMSS = (ms) => {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const SessionCountdownBanner = () => {
  const { isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const firedRef = useRef(false);

  // One interval for the component's lifetime (it never unmounts).
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Sign out exactly once when the deadline passes, then re-arm for the next session.
  useEffect(() => {
    if (!isAuthenticated) {
      firedRef.current = false; // disarm while hydrating or logged out
      return;
    }
    const expiry = tokenStore.getSessionExpiry();
    if (expiry == null) return; // no cap known → nothing to enforce
    if (nowMs >= expiry) {
      if (!firedRef.current) {
        firedRef.current = true;
        logout(); // clears storage + context state, best-effort server revoke
        navigate("/admin-login?reason=session_expired");
      }
    } else {
      firedRef.current = false; // live session → arm for the next zero-crossing
    }
  }, [nowMs, isAuthenticated, logout, navigate]);

  const expiry = tokenStore.getSessionExpiry();
  const remaining = expiry == null ? null : expiry - nowMs;
  const show =
    isAuthenticated &&
    remaining != null &&
    remaining > 0 &&
    remaining <= SESSION_WARNING_MS &&
    location.pathname !== "/";

  if (!show) return null;

  return (
    <div
      data-testid="session-countdown-banner"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100000,
        backgroundColor: "#1E344D",
        color: "#F9F4EA",
        fontFamily: "Poppins, sans-serif",
        fontSize: "14px",
        lineHeight: 1.4,
        textAlign: "center",
        padding: "10px 16px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
      }}
    >
      Your session ends in{" "}
      <span style={{ color: "#DE810E", fontWeight: 700 }}>
        {formatMMSS(remaining)}
      </span>
      . You'll be signed out automatically — save any changes now.
    </div>
  );
};

export default SessionCountdownBanner;
